// src/components/BudgetEntry.js
// Enter budget amounts per income-statement GL account for an academic year + term.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton, Button, Chip
} from '@mui/material';
import { Home, Save, AccountBalance } from '@mui/icons-material';
import axios from 'axios';
import { fmt, sym } from '../utils/currency';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const TERMS = [1, 2, 3];

const BudgetEntry = () => {
  const navigate = useNavigate();
  const [years, setYears]       = useState([]);
  const [academicYear, setYear] = useState('');
  const [term, setTerm]         = useState(1);
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [success, setSuccess]   = useState('');

  useEffect(() => {
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => {
        const ys = Array.isArray(r.data) ? r.data : [];
        setYears(ys);
        const active = ys.find(y => y.isActive) || ys[0];
        if (active) setYear(active.year);
      }).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    if (!academicYear || !term) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const { data } = await axios.get(`${API}/api/finance/budgets`, { headers: hdrs(), params: { academicYear, term } });
      setRows(data.rows || []);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load budgets'); }
    finally { setLoading(false); }
  }, [academicYear, term]);

  useEffect(() => { load(); }, [load]);

  const setAmount = (accountNo, value) => {
    setRows(prev => prev.map(r => r.accountNo === accountNo ? { ...r, amount: value } : r));
  };

  const save = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      const lines = rows.map(r => ({ accountNo: r.accountNo, amount: parseFloat(r.amount) || 0 }));
      await axios.post(`${API}/api/finance/budgets`, { academicYear, term, lines }, { headers: hdrs() });
      setSuccess('Budget saved.');
    } catch (err) { setError(err.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const section = (type) => rows.filter(r => r.accountType === type);
  const sectionTotal = (type) => section(type).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const renderSection = (title, type, color) => (
    <>
      <TableRow><TableCell colSpan={2} sx={{ fontWeight: 800, color, pt: 2 }}>{title}</TableCell></TableRow>
      {section(type).length === 0 ? (
        <TableRow><TableCell colSpan={2}><Typography variant="body2" color="text.secondary">No {title.toLowerCase()} accounts</Typography></TableCell></TableRow>
      ) : section(type).map(r => (
        <TableRow key={r.accountNo} hover>
          <TableCell>{r.accountNo} — {r.accountName}</TableCell>
          <TableCell align="right" sx={{ width: 200 }}>
            <TextField type="number" size="small" value={r.amount}
              onChange={e => setAmount(r.accountNo, e.target.value)}
              inputProps={{ step: '0.01', style: { textAlign: 'right' } }} sx={{ width: 160 }} />
          </TableCell>
        </TableRow>
      ))}
      <TableRow>
        <TableCell sx={{ fontWeight: 700 }}>Total {title}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{sym()} {fmt(sectionTotal(type))}</TableCell>
      </TableRow>
    </>
  );

  return (
    <Box sx={{ maxWidth: 820, mx: 'auto', mt: 4, mb: 6 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <AccountBalance sx={{ mr: 1, color: '#1565c0' }} />
              <Typography variant="h5" color="#1565c0" fontWeight="bold">Budget Entry</Typography>
            </Box>
            <IconButton onClick={() => navigate('/finance')} title="Finance Home"
              sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
            <TextField select label="Academic Year" value={academicYear} size="small" sx={{ minWidth: 140 }}
              onChange={e => setYear(e.target.value)}>
              {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
            </TextField>
            <TextField select label="Term" value={term} size="small" sx={{ minWidth: 100 }}
              onChange={e => setTerm(Number(e.target.value))}>
              {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
            </TextField>
            <Box flex={1} />
            <Button variant="contained" onClick={save} disabled={saving || loading || !rows.length}
              startIcon={saving ? <CircularProgress size={18} /> : <Save />}>
              {saving ? 'Saving…' : 'Save Budget'}
            </Button>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? <CircularProgress /> : rows.length === 0 ? (
            <Chip label="No Revenue/Expense accounts found" />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Account</strong></TableCell>
                  <TableCell align="right"><strong>Budget Amount</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {renderSection('Income', 'Revenue', '#2e7d32')}
                {renderSection('Expenses', 'Expense', '#c62828')}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BudgetEntry;
